import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ArrowLeft, ArrowRight } from 'lucide-react';
import StoreLayout, { PlaceholderImage, WhatsAppFloat, PriceDisplay, resolveImageSrc } from '../../components/store/StoreLayout';
import { useCart } from '../../contexts/CartContext';
import { formatRupee as fmt } from '@/lib/format';

export default function Cart() {
  const { items, updateQty, removeFromCart, totalValue } = useCart();
  const navigate = useNavigate();

  return (
    <StoreLayout>
      <WhatsAppFloat />
      <div className="max-w-5xl mx-auto px-4 py-10">
        <Link to="/shop" className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-amber-800 mb-6">
          <ArrowLeft className="w-4 h-4" /> Continue shopping
        </Link>
        <h1 className="text-3xl font-semibold" style={{ fontFamily: '"Playfair Display", serif' }}>Your cart</h1>
        <p className="text-sm text-stone-500 mt-1">{items.length} item{items.length === 1 ? '' : 's'}</p>

        {items.length === 0 ? (
          <div className="mt-10 text-center py-24 border border-dashed border-stone-200 rounded-lg">
            <p className="text-stone-500 mb-4">Your cart is empty.</p>
            <Link to="/shop" className="inline-flex items-center gap-2 bg-amber-700 hover:bg-amber-800 text-white px-5 py-2.5 rounded-full text-sm">
              Browse the shop <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <div className="mt-8 grid md:grid-cols-3 gap-8">
            <div className="md:col-span-2 space-y-3" data-testid="cart-items">
              {items.map(it => (
                <div key={it.product_id} className="flex gap-4 border border-stone-200 rounded-xl p-3" data-testid={`cart-item-${it.product_id}`}>
                  <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0 bg-stone-50">
                    {it.image_url
                      ? <img src={resolveImageSrc(it.image_url)} alt={it.product_name} className="w-full h-full object-cover" />
                      : <PlaceholderImage className="w-full h-full" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <Link to={`/product/${it.product_id}`} className="text-sm font-medium text-stone-900 hover:text-amber-800 line-clamp-2">
                      {it.product_name}
                    </Link>
                    <div className="text-[11px] text-stone-500">{[it.product_brand, it.product_size, it.product_color].filter(x => x && x !== 'NA').join(' · ')}</div>
                    <div className="mt-2 flex items-center justify-between">
                      <div className="inline-flex items-center border border-stone-300 rounded-full overflow-hidden">
                        <button onClick={() => updateQty(it.product_id, it.quantity - 1)} className="p-1.5 hover:bg-stone-100" aria-label="Decrement"><Minus className="w-3.5 h-3.5" /></button>
                        <span className="px-3 text-sm tabular-nums" data-testid={`cart-qty-${it.product_id}`}>{it.quantity}</span>
                        <button onClick={() => updateQty(it.product_id, it.quantity + 1)} className="p-1.5 hover:bg-stone-100" aria-label="Increment"><Plus className="w-3.5 h-3.5" /></button>
                      </div>
                      <div className="text-sm font-semibold text-amber-800">{fmt(it.quantity * it.selling_price)}</div>
                    </div>
                      <div className="text-[10px] text-stone-400 mt-0.5">
                        <PriceDisplay mrp={it.mrp} selling={it.selling_price} unit={it.unit} size="sm" />
                      </div>
                  </div>
                  <button onClick={() => removeFromCart(it.product_id)} className="text-stone-400 hover:text-red-700 self-start" data-testid={`cart-remove-${it.product_id}`} aria-label="Remove">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>

            {/* Summary */}
            <div className="md:col-span-1">
              <div className="border border-stone-200 rounded-xl p-5 sticky top-20">
                <div className="text-xs uppercase tracking-widest text-stone-500 mb-3">Order summary</div>
                <div className="flex justify-between text-sm">
                  <span className="text-stone-600">Subtotal</span>
                  <span className="tabular-nums font-medium" data-testid="cart-subtotal">{fmt(totalValue)}</span>
                </div>
                <div className="mt-1 text-[11px] text-stone-500 leading-relaxed">
                  Delivery charges + payment mode will be discussed with you on order confirmation.
                </div>
                <button
                  onClick={() => navigate('/checkout')}
                  data-testid="cart-checkout-btn"
                  className="w-full mt-5 bg-amber-700 hover:bg-amber-800 text-white py-3 rounded-full text-sm font-medium transition"
                >
                  Proceed to checkout
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </StoreLayout>
  );
}
